import React, { useRef, useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CircularProgress from "@mui/material/CircularProgress";

interface Props {
  storeId: number;
  currentLogo: string | null;
  storeName?: string;
  imgClassName?: string;
  fallbackClassName?: string;
  onLogoChange: (newLogo: string | null) => void;
}

const StoreLogoUpload: React.FC<Props> = ({ storeId, currentLogo, storeName, imgClassName, fallbackClassName,onLogoChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!statusMsg) return;
    const t = setTimeout(() => setStatusMsg(null), 2500);
    return () => clearTimeout(t);
  }, [statusMsg]);

  const logoUrl = currentLogo ? `${import.meta.env.VITE_API_URL.replace("/api", "")}${currentLogo}` : null;
  const initial = storeName?.[0]?.toUpperCase() ?? "S";

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) {
      setStatusMsg({ type: "error", text: "Only JPG, PNG, GIF, WEBP allowed." });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const MAX_MB = 5;
    if (file.size > MAX_MB * 1024 * 1024) {
      setStatusMsg({ type: "error", text: `Max file size is ${MAX_MB}MB.` });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const formData = new FormData();
    formData.append("profile_picture", file);
    setUploading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/stores/upload-logo/${storeId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data?.data?.logo) { onLogoChange(data.data.logo); setStatusMsg({ type: "success", text: "Logo updated!" }); }
      else if (data?.code === 401 || data?.message?.toLowerCase().includes("token")) {
        setStatusMsg({ type: "error", text: "⚠️ Session expired. Please login again." });
      } else {
        setStatusMsg({ type: "error", text: "Upload failed. Try again." });
      }
    } catch { setStatusMsg({ type: "error", text: "Upload failed. Try again." }); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleRemove = async () => {
    setUploading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/stores/update-store-profile/${storeId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ logo: "" }),
      });
      const data = await res.json();
      if (data?.success) { onLogoChange(null); setStatusMsg({ type: "success", text: "Logo removed." }); }
      else if (data?.code === 401 || data?.message?.toLowerCase().includes("token")) {
        setStatusMsg({ type: "error", text: "⚠️ Session expired. Please login again." });
      } else {
        setStatusMsg({ type: "error", text: "Remove failed. Try again." });
      }
    } catch { setStatusMsg({ type: "error", text: "Remove failed. Try again." }); }
    finally { setUploading(false); }
  };

  return (
    <>
      <div onClick={() => setOpen(true)} className="slu-trigger">
        {logoUrl ? (
          <img src={logoUrl} alt="logo" className={`${imgClassName ?? "stores-logo-img"} slu-trigger-img`} onError={(e) => { (e.target as HTMLImageElement).src = "/images/your-company-lsiting.png"; }} />
        ) : (
          <div className={`${fallbackClassName ?? "stores-logo-fallback"} slu-trigger-img`}>{initial}</div>
        )}
        <div className="slu-trigger-overlay"><EditOutlinedIcon style={{ fontSize: 14, color: "#fff" }} /></div>
      </div>

      <Dialog open={open} onClose={() => !uploading && setOpen(false)} PaperProps={{ className: "slu-paper" }} disableScrollLock>
          <IconButton onClick={() => { setOpen(false); setStatusMsg(null); }} disabled={uploading} size="small" className="slu-close">
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
        <DialogContent className="slu-content">
          <div className="slu-title">Edit Logo<span className="slu-subtitle">Store #{storeId}</span></div>
          <div className="slu-preview">
            {logoUrl ? (
              <img src={logoUrl} alt="logo" className="slu-img" onError={(e) => { (e.target as HTMLImageElement).src = "/images/your-company-lsiting.png"; }} />
            ) : (
              <div className="slu-initial">{initial}</div>
            )}
            {uploading && <CircularProgress size={96} thickness={2} className="slu-spinner" />}
          </div>
          <div className="slu-actions">
            <Tooltip title={logoUrl ? "Change logo" : "Upload logo"} arrow>
              <span><IconButton onClick={() => fileInputRef.current?.click()} disabled={uploading} className="slu-btn slu-btn--edit"><EditOutlinedIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            {logoUrl && (
              <Tooltip title="Remove logo" arrow>
                <span><IconButton onClick={handleRemove} disabled={uploading} className="slu-btn slu-btn--delete"><DeleteOutlineOutlinedIcon fontSize="small" /></IconButton></span>
              </Tooltip>
            )}
          </div>
          <div className="slu-status-wrap">
            <div className={`slu-status ${uploading || statusMsg ? "slu-status--visible" : ""} ${!uploading && statusMsg?.type === "error" ? "slu-status--error" : ""} ${statusMsg && !uploading ? "slu-status--animate" : ""}`}>
              {uploading ? "Processing..." : statusMsg ? `${statusMsg.type === "success" ? "✓" : "✕"} ${statusMsg.text}` : "\u200b"}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" ref={fileInputRef} style={{ display: "none" }} onChange={handleUpload} />
    </>
  );
};

export default StoreLogoUpload;